import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Building,
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Clock,
  TrendingUp,
  Target,
  FileText,
  MessageSquare,
  CheckCircle,
  Plus,
  Edit,
  Save,
  X,
  ExternalLink,
  AlertTriangle,
  Loader2,
  ChevronRight,
  Users,
  Activity,
  Briefcase,
  Timer,
  Upload,
  Download,
  Trash2,
  MoreVertical
} from 'lucide-react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { api } from '@/lib/api';
import { Lead, LeadNote, Task } from '@/types/lead';
import { useAuth } from '@/contexts/AuthContext';
import { ReassignLeadModal } from '@/components/ReassignLeadModal';
import { GrantPortalAccessModal } from '@/components/GrantPortalAccessModal';
import { useToast } from '@/hooks/use-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Stage color mapping
const getStageColor = (stage: string) => {
  const colors: Record<string, string> = {
    NEW: 'bg-blue-100 text-blue-800 border-blue-200',
    CONTACTED: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    DISCUSSION: 'bg-purple-100 text-purple-800 border-purple-200',
    PROPOSAL: 'bg-amber-100 text-amber-800 border-amber-200',
    NEGOTIATION: 'bg-orange-100 text-orange-800 border-orange-200',
    WON: 'bg-green-100 text-green-800 border-green-200',
    LOST: 'bg-red-100 text-red-800 border-red-200',
  };
  return colors[stage] || 'bg-gray-100 text-gray-800 border-gray-200';
};

// Priority color mapping
const getPriorityColor = (priority: string) => {
  const colors: Record<string, string> = {
    HOT: 'bg-red-100 text-red-700 border-red-200',
    WARM: 'bg-orange-100 text-orange-700 border-orange-200',
    COLD: 'bg-blue-100 text-blue-700 border-blue-200',
  };
  return colors[priority] || 'bg-gray-100 text-gray-700 border-gray-200';
};

// Task status color
const getTaskStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    PENDING: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    IN_PROGRESS: 'bg-blue-100 text-blue-700 border-blue-200',
    COMPLETED: 'bg-green-100 text-green-700 border-green-200',
    CANCELLED: 'bg-gray-100 text-gray-700 border-gray-200',
  };
  return colors[status] || 'bg-gray-100 text-gray-700 border-gray-200';
};

// Task priority color
const getTaskPriorityColor = (priority: string) => {
  const colors: Record<string, string> = {
    URGENT: 'bg-red-100 text-red-700',
    HIGH: 'bg-orange-100 text-orange-700',
    MEDIUM: 'bg-yellow-100 text-yellow-700',
    LOW: 'bg-green-100 text-green-700',
  };
  return colors[priority] || 'bg-gray-100 text-gray-700';
};

// Activity type icon mapping
const getActivityIcon = (type: string) => {
  const icons: Record<string, React.ReactNode> = {
    LEAD_CREATED: <Plus className="h-4 w-4 text-blue-500" />,
    LEAD_UPDATED: <Edit className="h-4 w-4 text-gray-500" />,
    LEAD_STAGE_CHANGED: <TrendingUp className="h-4 w-4 text-purple-500" />,
    LEAD_CLOSED_WON: <CheckCircle className="h-4 w-4 text-green-500" />,
    LEAD_CLOSED_LOST: <X className="h-4 w-4 text-red-500" />,
    NOTE_ADDED: <FileText className="h-4 w-4 text-gray-500" />,
    TASK_CREATED: <CheckCircle className="h-4 w-4 text-orange-500" />,
    TASK_COMPLETED: <CheckCircle className="h-4 w-4 text-green-500" />,
    CALL: <Phone className="h-4 w-4 text-blue-500" />,
    MESSAGE: <MessageSquare className="h-4 w-4 text-green-500" />,
    FILE_UPLOADED: <Upload className="h-4 w-4 text-purple-500" />,
    STATUS_CHANGE: <Activity className="h-4 w-4 text-amber-500" />,
    WHATSAPP_SENT: <MessageSquare className="h-4 w-4 text-green-600" />,
    WHATSAPP_RECEIVED: <MessageSquare className="h-4 w-4 text-green-600" />,
  };
  return icons[type] || <Activity className="h-4 w-4 text-gray-400" />;
};

// Format relative time
const formatRelativeTime = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

// Format currency
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(value);
};

export const LeadDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [showPortalAccessModal, setShowPortalAccessModal] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [newActivity, setNewActivity] = useState({ type: 'NOTE', content: '' });
  const [newTask, setNewTask] = useState({ title: '', description: '', dueDate: '', priority: 'MEDIUM' });
  const [editedLead, setEditedLead] = useState<Partial<Lead>>({});

  // Fetch lead details
  const { data: lead, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['lead-detail', id],
    queryFn: async () => {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/leads/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch lead details');
      }
      const data = await response.json();
      return data.success ? data.data : data;
    },
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: true,
  });

  // Update editedLead when lead data changes
  useEffect(() => {
    if (lead) {
      setEditedLead(lead);
    }
  }, [lead]);

  // Add note mutation
  const addNoteMutation = useMutation({
    mutationFn: async (content: string) => {
      return api.addNote(id!, content);
    },
    onSuccess: () => {
      setNewNote('');
      refetch();
      toast({ title: 'Note added successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to add note', description: error.message, variant: 'destructive' });
    },
  });

  // Add activity mutation
  const addActivityMutation = useMutation({
    mutationFn: async (data: { type: string; content: string }) => {
      return api.addActivity(id!, { ...data, createdBy: user?.name || 'Unknown' });
    },
    onSuccess: () => {
      setNewActivity({ type: 'NOTE', content: '' });
      refetch();
      toast({ title: 'Activity added successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to add activity', description: error.message, variant: 'destructive' });
    },
  });

  // Add task mutation
  const addTaskMutation = useMutation({
    mutationFn: async (data: { title: string; description?: string; dueDate: string; priority: string }) => {
      return api.createTask(id!, data);
    },
    onSuccess: () => {
      setNewTask({ title: '', description: '', dueDate: '', priority: 'MEDIUM' });
      refetch();
      toast({ title: 'Task created successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to create task', description: error.message, variant: 'destructive' });
    },
  });

  // Update lead mutation
  const updateLeadMutation = useMutation({
    mutationFn: async (data: Partial<Lead>) => {
      return api.updateLead(id!, data);
    },
    onSuccess: () => {
      setIsEditing(false);
      refetch();
      toast({ title: 'Lead updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to update lead', description: error.message, variant: 'destructive' });
    },
  });

  // Handle reassign
  const handleReassign = async (newOwnerId: string, note?: string) => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/leads/${id}/reassign`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ newOwnerId, note })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to reassign lead');
      }

      setShowReassignModal(false);
      refetch();
      toast({ title: 'Lead reassigned successfully' });
    } catch (error: any) {
      toast({ title: 'Failed to reassign lead', description: error.message, variant: 'destructive' });
      throw error;
    }
  };

  // Calculate days in pipeline
  const getDaysInPipeline = () => {
    if (!lead?.createdAt) return 0;
    const created = new Date(lead.createdAt);
    const now = new Date();
    return Math.ceil((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));
  };

  // Calculate days in current stage
  const getDaysInStage = () => {
    if (!lead?.stageUpdatedAt) return 0;
    const stageDate = new Date(lead.stageUpdatedAt);
    const now = new Date();
    return Math.ceil((now.getTime() - stageDate.getTime()) / (1000 * 60 * 60 * 24));
  };

  // Calculate days since last activity
  const getDaysSinceLastActivity = () => {
    if (!lead?.lastActivity) return null;
    const lastActivity = new Date(lead.lastActivity);
    const now = new Date();
    return Math.ceil((now.getTime() - lastActivity.getTime()) / (1000 * 60 * 60 * 24));
  };

  // Parse user info from activity metadata
  const parseActivityUser = (activity: any) => {
    try {
      if (activity.metadata) {
        const meta = typeof activity.metadata === 'string'
          ? JSON.parse(activity.metadata)
          : activity.metadata;
        return meta.userName || meta.createdBy || activity.createdBy || 'System';
      }
    } catch (e) { /* ignore */ }
    return activity.createdBy || 'System';
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-blue-500 mx-auto mb-4" />
            <p className="text-gray-600">Loading lead details...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !lead) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Lead Not Found</h2>
            <p className="text-gray-600 mb-4">
              {error instanceof Error ? error.message : 'The lead you are looking for does not exist or you do not have permission to view it.'}
            </p>
            <div className="flex items-center justify-center gap-3">
              <Button variant="outline" onClick={() => refetch()}>
                <Loader2 className="h-4 w-4 mr-2" />
                Retry
              </Button>
              <Button onClick={() => navigate('/pipeline')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Pipeline
              </Button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const daysSinceActivity = getDaysSinceLastActivity();

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Background Refetch Indicator */}
        {isFetching && !isLoading && (
          <div className="mb-4 flex items-center gap-2 text-sm text-blue-600 bg-blue-50 px-3 py-2 rounded-lg">
            <Loader2 className="h-4 w-4 animate-spin" />
            Updating...
          </div>
        )}

        {/* Breadcrumb Navigation */}
        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6" aria-label="Breadcrumb">
          <Link to="/dashboard" className="hover:text-gray-700">Dashboard</Link>
          <ChevronRight className="h-4 w-4" />
          <Link to="/pipeline" className="hover:text-gray-700">Pipeline</Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-gray-900 font-medium">{lead.name}</span>
        </nav>

        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6"
        >
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              {/* Lead Avatar */}
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <Building className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
              </div>

              {/* Lead Info */}
              <div>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{lead.name}</h1>
                  <Badge className={`${getStageColor(lead.stage)}`}>{lead.stage}</Badge>
                  <Badge className={`${getPriorityColor(lead.priority)}`}>{lead.priority}</Badge>
                </div>
                <p className="text-lg text-gray-600 mb-2">{lead.company}</p>
                <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-sm text-gray-500">
                  {lead.email && (
                    <a href={`mailto:${lead.email}`} className="flex items-center gap-1 hover:text-blue-600">
                      <Mail className="h-4 w-4" />
                      {lead.email}
                    </a>
                  )}
                  {lead.phoneNumber && (
                    <a href={`tel:${lead.phoneNumber}`} className="flex items-center gap-1 hover:text-blue-600">
                      <Phone className="h-4 w-4" />
                      {lead.phoneNumber}
                    </a>
                  )}
                  {lead.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      {lead.location}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit Lead
              </Button>
              {user?.role === 'CEO' && (
                <Button variant="outline" size="sm" onClick={() => setShowReassignModal(true)}>
                  <Users className="h-4 w-4 mr-2" />
                  Reassign
                </Button>
              )}
              {(user?.role === 'CEO' || user?.role === 'GROUP_MANAGER' || user?.role === 'MANAGER') && (
                <Button variant="outline" size="sm" onClick={() => setShowPortalAccessModal(true)}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Portal Access
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" aria-label="More actions">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate('/pipeline')}>
                    View in Pipeline
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate(`/clients/${id}/deliverables`)}>
                    View Deliverables
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Quick Stats Row */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 mt-6 pt-6 border-t border-gray-100">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(lead.dealValue || lead.value || 0)}
              </p>
              <p className="text-sm text-gray-500">Deal Value</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{getDaysInPipeline()}</p>
              <p className="text-sm text-gray-500">Days in Pipeline</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{getDaysInStage()}</p>
              <p className="text-sm text-gray-500">Days in Stage</p>
            </div>
            <div className="text-center">
              <p className={`text-2xl font-bold ${daysSinceActivity && daysSinceActivity > 7 ? 'text-red-600' : 'text-gray-900'}`}>
                {daysSinceActivity !== null ? daysSinceActivity : '-'}
              </p>
              <p className="text-sm text-gray-500">Days Since Activity</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">
                {lead._count?.activities || lead.activities?.length || 0}
              </p>
              <p className="text-sm text-gray-500">Total Activities</p>
            </div>
          </div>

          {/* Warning Banner */}
          {daysSinceActivity && daysSinceActivity > 3 && (
            <div className={`mt-4 p-3 rounded-lg flex items-center gap-2 ${
              daysSinceActivity > 7 ? 'bg-red-50 text-red-700' : 'bg-yellow-50 text-yellow-700'
            }`}>
              <AlertTriangle className="h-5 w-5" />
              <span className="font-medium">
                {daysSinceActivity > 7
                  ? `Urgent: No activity for ${daysSinceActivity} days. This lead needs immediate attention!`
                  : `Warning: No activity for ${daysSinceActivity} days. Consider following up.`
                }
              </span>
            </div>
          )}
        </motion.div>

        {/* Tabs Section */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-gray-200 p-1 rounded-lg">
            <TabsTrigger value="overview" className="px-4">Overview</TabsTrigger>
            <TabsTrigger value="timeline" className="px-4">
              Timeline ({lead.activities?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="tasks" className="px-4">
              Tasks ({lead.tasks?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="notes" className="px-4">
              Notes ({lead.notes?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="files" className="px-4">Files</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Lead Details */}
              <div className="lg:col-span-2 space-y-6">
                {/* Contact Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="h-5 w-5" />
                      Contact Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Contact Person</label>
                      <p className="text-gray-900">{lead.contactPerson || '-'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Email</label>
                      <p className="text-gray-900">{lead.email || '-'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Phone</label>
                      <p className="text-gray-900">{lead.phoneNumber || '-'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">LinkedIn</label>
                      {lead.linkedinUrl ? (
                        <a href={lead.linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                          View Profile <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <p className="text-gray-900">-</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Company Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building className="h-5 w-5" />
                      Company Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Company Name</label>
                      <p className="text-gray-900">{lead.company}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Industry</label>
                      <p className="text-gray-900">{lead.industry || '-'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Company Size</label>
                      <p className="text-gray-900">{lead.companySize || '-'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Location</label>
                      <p className="text-gray-900">{lead.location || '-'}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Deal Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="h-5 w-5" />
                      Deal Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Deal Value</label>
                      <p className="text-gray-900 text-lg font-semibold">
                        {formatCurrency(lead.dealValue || lead.value || 0)}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Expected Close Date</label>
                      <p className="text-gray-900">
                        {lead.expectedClose
                          ? new Date(lead.expectedClose).toLocaleDateString()
                          : '-'
                        }
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Current Stage</label>
                      <Badge className={`${getStageColor(lead.stage)} mt-1`}>{lead.stage}</Badge>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Priority</label>
                      <Badge className={`${getPriorityColor(lead.priority)} mt-1`}>{lead.priority}</Badge>
                    </div>
                  </CardContent>
                </Card>

                {/* Recent Activity (Mini Timeline) */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Activity className="h-5 w-5" />
                      Recent Activity
                    </CardTitle>
                    <Button variant="link" size="sm" onClick={() => setActiveTab('timeline')}>
                      View All
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {lead.activities && lead.activities.length > 0 ? (
                      <div className="space-y-4">
                        {lead.activities.slice(0, 5).map((activity: any) => (
                          <div key={activity.id} className="flex items-start gap-3">
                            <div className="mt-1">{getActivityIcon(activity.type)}</div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-gray-900">{activity.description}</p>
                              <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                                <span>by {parseActivityUser(activity)}</span>
                                <span>-</span>
                                <span>{formatRelativeTime(activity.createdAt)}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-gray-500">
                        <Activity className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                        <p>No activities yet</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Right Column - Owner & Timeline */}
              <div className="space-y-6">
                {/* Lead Owner */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="h-5 w-5" />
                      Lead Owner
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-lg font-medium">
                          {lead.assignedToUser?.name?.charAt(0) || 'U'}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {lead.assignedToUser?.name || 'Unassigned'}
                        </p>
                        <p className="text-sm text-gray-500 truncate">
                          {lead.assignedToUser?.email || '-'}
                        </p>
                        {lead.assignedToUser?.role && (
                          <Badge variant="outline" className="text-xs mt-1">
                            {lead.assignedToUser.role.replace('_', ' ')}
                          </Badge>
                        )}
                      </div>
                    </div>
                    {user?.role === 'CEO' && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full mt-3"
                        onClick={() => setShowReassignModal(true)}
                      >
                        <Users className="h-4 w-4 mr-2" />
                        Change Owner
                      </Button>
                    )}
                  </CardContent>
                </Card>

                {/* Timeline Summary */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="h-5 w-5" />
                      Timeline
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Created</span>
                      <span className="text-sm font-medium">
                        {new Date(lead.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Last Updated</span>
                      <span className="text-sm font-medium">
                        {new Date(lead.updatedAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Stage Changed</span>
                      <span className="text-sm font-medium">
                        {new Date(lead.stageUpdatedAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Last Activity</span>
                      <span className="text-sm font-medium">
                        {lead.lastActivity
                          ? new Date(lead.lastActivity).toLocaleDateString()
                          : '-'
                        }
                      </span>
                    </div>
                  </CardContent>
                </Card>

                {/* Quick Actions */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Briefcase className="h-5 w-5" />
                      Quick Actions
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {lead.phoneNumber && (
                      <Button variant="outline" className="w-full justify-start" asChild>
                        <a href={`tel:${lead.phoneNumber}`}>
                          <Phone className="h-4 w-4 mr-2" />
                          Call Lead
                        </a>
                      </Button>
                    )}
                    {lead.email && (
                      <Button variant="outline" className="w-full justify-start" asChild>
                        <a href={`mailto:${lead.email}`}>
                          <Mail className="h-4 w-4 mr-2" />
                          Send Email
                        </a>
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => setActiveTab('tasks')}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Add Task
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => setActiveTab('notes')}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Add Note
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Timeline Tab */}
          <TabsContent value="timeline">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Activity Form */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle>Add Activity</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select
                      value={newActivity.type}
                      onChange={(e) => setNewActivity({ ...newActivity, type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      aria-label="Activity type"
                    >
                      <option value="NOTE">Note</option>
                      <option value="CALL">Call</option>
                      <option value="MESSAGE">Message</option>
                      <option value="STATUS_CHANGE">Status Update</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <Textarea
                      placeholder="Enter activity details..."
                      value={newActivity.content}
                      onChange={(e) => setNewActivity({ ...newActivity, content: e.target.value })}
                      rows={3}
                    />
                  </div>
                  <Button
                    className="w-full"
                    onClick={() => newActivity.content && addActivityMutation.mutate(newActivity)}
                    disabled={!newActivity.content || addActivityMutation.isPending}
                  >
                    {addActivityMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4 mr-2" />
                    )}
                    Add Activity
                  </Button>
                </CardContent>
              </Card>

              {/* Activity Timeline */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Activity Timeline ({lead.activities?.length || 0})</CardTitle>
                </CardHeader>
                <CardContent>
                  {lead.activities && lead.activities.length > 0 ? (
                    <div className="relative">
                      {/* Timeline line */}
                      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />

                      <div className="space-y-6">
                        {lead.activities.map((activity: any, index: number) => (
                          <motion.div
                            key={activity.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="relative flex items-start gap-4 pl-10"
                          >
                            {/* Timeline dot */}
                            <div className="absolute left-2 w-4 h-4 bg-white border-2 border-blue-500 rounded-full" />

                            <div className="flex-1 bg-gray-50 rounded-lg p-4">
                              <div className="flex items-start justify-between">
                                <div className="flex items-center gap-2">
                                  {getActivityIcon(activity.type)}
                                  <Badge variant="outline" className="text-xs">
                                    {activity.type.replace(/_/g, ' ')}
                                  </Badge>
                                </div>
                                <span className="text-xs text-gray-500">
                                  {formatRelativeTime(activity.createdAt)}
                                </span>
                              </div>
                              <p className="mt-2 text-sm text-gray-900">{activity.description}</p>
                              <p className="mt-1 text-xs text-gray-500">
                                by {parseActivityUser(activity)} - {new Date(activity.createdAt).toLocaleString()}
                              </p>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <Activity className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p className="text-lg font-medium mb-1">No activities yet</p>
                      <p className="text-sm">Add your first activity to start tracking this lead</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Task Form */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle>Create Task</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <Input
                      placeholder="Task title..."
                      value={newTask.title}
                      onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <Textarea
                      placeholder="Task description (optional)..."
                      value={newTask.description}
                      onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                      rows={2}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                    <Input
                      type="date"
                      value={newTask.dueDate}
                      onChange={(e) => setNewTask({ ...newTask, dueDate: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                    <select
                      value={newTask.priority}
                      onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      aria-label="Task priority"
                    >
                      <option value="LOW">Low</option>
                      <option value="MEDIUM">Medium</option>
                      <option value="HIGH">High</option>
                      <option value="URGENT">Urgent</option>
                    </select>
                  </div>
                  <Button
                    className="w-full"
                    onClick={() => newTask.title && newTask.dueDate && addTaskMutation.mutate(newTask)}
                    disabled={!newTask.title || !newTask.dueDate || addTaskMutation.isPending}
                  >
                    {addTaskMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4 mr-2" />
                    )}
                    Create Task
                  </Button>
                </CardContent>
              </Card>

              {/* Tasks List */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Tasks ({lead.tasks?.length || 0})</CardTitle>
                </CardHeader>
                <CardContent>
                  {lead.tasks && lead.tasks.length > 0 ? (
                    <div className="space-y-4">
                      {lead.tasks.map((task: any) => (
                        <div key={task.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <CheckCircle className={`h-4 w-4 ${task.status === 'COMPLETED' ? 'text-green-500' : 'text-gray-400'}`} />
                                <h4 className={`font-medium ${task.status === 'COMPLETED' ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                                  {task.title}
                                </h4>
                              </div>
                              {task.description && (
                                <p className="text-sm text-gray-600 ml-6 mb-2">{task.description}</p>
                              )}
                              <div className="flex items-center gap-2 ml-6">
                                <Badge className={getTaskStatusColor(task.status)}>
                                  {task.status}
                                </Badge>
                                <Badge className={getTaskPriorityColor(task.priority)}>
                                  {task.priority}
                                </Badge>
                                <span className="text-xs text-gray-500">
                                  Due: {new Date(task.dueDate).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <CheckCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p className="text-lg font-medium mb-1">No tasks yet</p>
                      <p className="text-sm">Create your first task to start managing this lead</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Notes Tab */}
          <TabsContent value="notes">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Note Form */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle>Add Note</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Note Content</label>
                    <Textarea
                      placeholder="Write your note..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      rows={5}
                    />
                  </div>
                  <Button
                    className="w-full"
                    onClick={() => newNote && addNoteMutation.mutate(newNote)}
                    disabled={!newNote || addNoteMutation.isPending}
                  >
                    {addNoteMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4 mr-2" />
                    )}
                    Add Note
                  </Button>
                </CardContent>
              </Card>

              {/* Notes List */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Notes ({lead.notes?.length || 0})</CardTitle>
                </CardHeader>
                <CardContent>
                  {lead.notes && lead.notes.length > 0 ? (
                    <div className="space-y-4">
                      {lead.notes.map((note: any) => (
                        <div key={note.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <p className="text-gray-900 whitespace-pre-wrap">{note.content}</p>
                          <div className="flex items-center gap-2 mt-3 text-xs text-gray-500">
                            <User className="h-3 w-3" />
                            <span>{note.author?.name || 'Unknown'}</span>
                            <span>-</span>
                            <span>{new Date(note.createdAt).toLocaleString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p className="text-lg font-medium mb-1">No notes yet</p>
                      <p className="text-sm">Add your first note to document this lead</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Files Tab */}
          <TabsContent value="files">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Files & Attachments</CardTitle>
                <Button variant="outline" size="sm" disabled aria-label="Upload file (coming soon)">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload File (Coming Soon)
                </Button>
              </CardHeader>
              <CardContent>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                  <Upload className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600 mb-2">Drag and drop files here, or click to browse</p>
                  <p className="text-sm text-gray-500">Supports PDF, DOC, XLS, PPT, and image files up to 10MB</p>
                </div>
                {/* File list would go here */}
                <div className="mt-6 text-center text-gray-500">
                  <FileText className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  <p>No files uploaded yet</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Edit Lead Modal */}
        <AnimatePresence>
          {isEditing && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
              role="dialog"
              aria-label="Edit lead details"
              aria-modal="true"
              onClick={() => setIsEditing(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold">Edit Lead</h2>
                    <Button variant="ghost" size="sm" onClick={() => setIsEditing(false)} aria-label="Close edit dialog">
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <Input
                        value={editedLead.name || ''}
                        onChange={(e) => setEditedLead({ ...editedLead, name: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                      <Input
                        value={editedLead.company || ''}
                        onChange={(e) => setEditedLead({ ...editedLead, company: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <Input
                        value={editedLead.email || ''}
                        onChange={(e) => setEditedLead({ ...editedLead, email: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <Input
                        value={editedLead.phoneNumber || ''}
                        onChange={(e) => setEditedLead({ ...editedLead, phoneNumber: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Deal Value</label>
                      <Input
                        type="number"
                        value={editedLead.dealValue || ''}
                        onChange={(e) => setEditedLead({ ...editedLead, dealValue: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Expected Close</label>
                      <Input
                        type="date"
                        value={editedLead.expectedClose ? editedLead.expectedClose.split('T')[0] : ''}
                        onChange={(e) => setEditedLead({ ...editedLead, expectedClose: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Stage</label>
                      <select
                        value={editedLead.stage || 'NEW'}
                        onChange={(e) => setEditedLead({ ...editedLead, stage: e.target.value as Lead['stage'] })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        aria-label="Lead stage"
                      >
                        <option value="NEW">New</option>
                        <option value="CONTACTED">Contacted</option>
                        <option value="DISCUSSION">Discussion</option>
                        <option value="PROPOSAL">Proposal</option>
                        <option value="NEGOTIATION">Negotiation</option>
                        <option value="WON">Won</option>
                        <option value="LOST">Lost</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                      <select
                        value={editedLead.priority || 'WARM'}
                        onChange={(e) => setEditedLead({ ...editedLead, priority: e.target.value as Lead['priority'] })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        aria-label="Lead priority"
                      >
                        <option value="HOT">Hot</option>
                        <option value="WARM">Warm</option>
                        <option value="COLD">Cold</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                      <Input
                        value={editedLead.contactPerson || ''}
                        onChange={(e) => setEditedLead({ ...editedLead, contactPerson: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                      <Input
                        value={editedLead.location || ''}
                        onChange={(e) => setEditedLead({ ...editedLead, location: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
                      <Input
                        value={editedLead.industry || ''}
                        onChange={(e) => setEditedLead({ ...editedLead, industry: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Company Size</label>
                      <Input
                        value={editedLead.companySize || ''}
                        onChange={(e) => setEditedLead({ ...editedLead, companySize: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
                <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setIsEditing(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={() => updateLeadMutation.mutate(editedLead)}
                    disabled={updateLeadMutation.isPending}
                  >
                    {updateLeadMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Save Changes
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Reassign Modal */}
        {lead && (
          <ReassignLeadModal
            open={showReassignModal}
            onClose={() => setShowReassignModal(false)}
            lead={lead}
            onReassign={handleReassign}
          />
        )}

        {/* Portal Access Modal */}
        {lead && showPortalAccessModal && (
          <GrantPortalAccessModal
            leadId={lead.id}
            leadEmail={lead.email || ''}
            onClose={() => setShowPortalAccessModal(false)}
            onSuccess={() => setShowPortalAccessModal(false)}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default LeadDetailPage;
